import type { Dispatch, SetStateAction } from 'react';

import { useEffect, useMemo, useState } from 'react';

import { useSizeContext } from '~/utilities/context/dynamic/SizeContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import type { Maybe, Size } from '~/utilities/graphql/codegen';
import { parseDoubleSizes, parseSelectedDoubleSize } from '~/utilities/parsers';

type Params = {
  disableBackInStockPopup: boolean;
  isDoubleSize: boolean;
  setBackInStockDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsOutOfStockSizeSelected: (value: boolean) => void;
  setSelectedSize: (value?: Size | null) => void;
  sizes: Maybe<Size>[];
};

const ShowBackInStockDialog: Record<SiteType, boolean> = {
  [SiteType.Aaf]: true,
  [SiteType.Employeeshop]: true,
  [SiteType.Gstar]: true,
  [SiteType.Outlet]: false,
  [SiteType.Wahts]: true,
};

export const useSelectSize = ({
  disableBackInStockPopup,
  isDoubleSize,
  setBackInStockDialogOpen,
  setIsOutOfStockSizeSelected,
  setSelectedSize,
  sizes,
}: Params) => {
  const { siteType } = useAppContext();
  const { setSizeCode, gridValues, setGridValues } = useSizeContext();

  const { waistSizes, lengthSizes } = parseDoubleSizes(sizes);
  const [parsedWaistSizes, setParsedWaistSizes] = useState(waistSizes);
  const [parsedLengthSizes, setParsedLengthSizes] = useState(lengthSizes);
  const [activatable, setActivatable] = useState(true);

  useEffect(() => {
    setParsedWaistSizes(waistSizes);
    setParsedLengthSizes(lengthSizes);
    // Including waistSizes and lengthSizes to the dependencies array, will lead to infinite update,
    // because these variables changes on every render. Instead sizeInformation is used as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizes]);

  useEffect(() => {
    if (!isDoubleSize) {
      return;
    }

    const { gridValue1, gridValue2 } = gridValues;

    const handleSizeState = (
      parsedSizes: Record<string, { purchasable?: boolean }>,
      gridValue: string,
      setIsOutOfStockSizeSelected: (value: boolean) => void
    ) => {
      const isSizeAvailable = Object.entries(parsedSizes).reduce((acc, [sizeValue, sizeInfo]) => {
        if (sizeValue !== gridValue) {
          return acc;
        }

        return !!sizeInfo.purchasable;
      }, false);

      setIsOutOfStockSizeSelected(!isSizeAvailable);
    };

    if (gridValue1) {
      handleSizeState(parsedWaistSizes, gridValue1, setIsOutOfStockSizeSelected);
    }

    if (gridValue2) {
      handleSizeState(parsedLengthSizes, gridValue2, setIsOutOfStockSizeSelected);
    }
  }, [gridValues, parsedLengthSizes, parsedWaistSizes, setIsOutOfStockSizeSelected, isDoubleSize]);

  const filteredSizes = useMemo(
    () => sizes.filter(size => size?.willBeAvailable !== false),
    [sizes]
  );

  const filteredParsedWaistSizes = useMemo(
    () =>
      Object.entries(parsedWaistSizes)
        .filter(([, sizeInfo]) => sizeInfo?.willBeAvailable !== false)
        .map(([value, sizeInfo]) => ({ value, ...sizeInfo })),
    [parsedWaistSizes]
  );

  const filteredParsedLengthSizes = useMemo(
    () =>
      Object.entries(parsedLengthSizes)
        .filter(([, sizeInfo]) => sizeInfo?.willBeAvailable !== false)
        .map(([value, sizeInfo]) => ({ value, ...sizeInfo })),
    [parsedLengthSizes]
  );

  const selectSize = (size?: Size | null) => {
    const isCurrentlySelected =
      activatable && size?.gridValue1?.toUpperCase() === gridValues.gridValue1;

    if (isCurrentlySelected) {
      return;
    }

    setGridValues(prev => ({ ...prev, gridValue1: size?.value || '' }));
    setSelectedSize(size);

    if (!size?.purchasable && size?.willBeAvailable && ShowBackInStockDialog[siteType]) {
      if (!disableBackInStockPopup) {
        setBackInStockDialogOpen(true);
        setActivatable(false);
      }
    } else {
      setSizeCode(size?.code || '');
    }
  };

  const selectDoubleSize = (sizeCodeValue: string, size: Size, isWaist: boolean) => {
    const currentGridValue = isWaist ? gridValues.gridValue1 : gridValues.gridValue2;
    const isCurrentlySelected = activatable && sizeCodeValue.toUpperCase() === currentGridValue;

    if (isCurrentlySelected) {
      return;
    }

    setSelectedSize(size);

    const selectedGridValues = {
      gridValue1: isWaist ? sizeCodeValue : gridValues.gridValue1 || '',
      gridValue2: isWaist ? gridValues.gridValue2 || '' : sizeCodeValue,
    };

    const rearrangeSizes = () => {
      const parsedSizes = parseSelectedDoubleSize(sizeCodeValue, sizes, isWaist);
      const selectedSize = sizes.find(
        size =>
          size?.value === (selectedGridValues.gridValue1 || '') + selectedGridValues.gridValue2
      );

      setSizeCode(prev => selectedSize?.code || prev);

      if (isWaist) {
        setParsedLengthSizes(parsedSizes);
      } else {
        setParsedWaistSizes(parsedSizes);
      }
    };

    setGridValues(selectedGridValues);

    if (!size?.purchasable && size?.willBeAvailable && ShowBackInStockDialog[siteType]) {
      if (!disableBackInStockPopup) {
        setBackInStockDialogOpen(true);
        setActivatable(false);
      }
    } else {
      rearrangeSizes();
    }
  };

  return {
    activatable,
    filteredParsedLengthSizes,
    filteredParsedWaistSizes,
    filteredSizes,
    parsedLengthSizes,
    selectDoubleSize,
    selectSize,
    setActivatable,
  };
};
